import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import {
  isSameSpan,
  movedSpan,
  offsetToRawMinutes,
  resizedSpan,
  CALENDAR_DRAG_THRESHOLD_PX,
  type CalendarBlock,
  type ISODate,
} from '@app/shared'

// ============================================================================
// MOVING AND RESIZING A BLOCK — the pointer half. The arithmetic lives in @app/shared
// (`movedSpan`, `resizedSpan`), so this file owns exactly one thing: turning pointer positions
// into a proposal, and a release into at most one write.
//
// WHY POINTER EVENTS AND NOT HTML5 DRAG-AND-DROP. The Unscheduled rail drags with the native
// API, and that is right for it: a task is being carried from one region into another, and the
// browser's drag image is the whole affordance. A block is not carried anywhere — it stays on
// the grid it is already on and follows the pointer by the quarter-hour. Native DnD gives no
// reliable live coordinate during the drag (dragover fires on the drop target, not the source,
// and Firefox has historically zeroed clientX/Y), and a drag image would double the block.
//
// FOUR RULES THIS FILE KEEPS:
//   1. A PRESS THAT NEVER TRAVELLED IS A CLICK. Nothing happens until the pointer has moved
//      CALENDAR_DRAG_THRESHOLD_PX, so click → inline edit survives intact. `consumeClick()` is
//      how the block tells the difference after the fact.
//   2. A DRAG THAT LANDED WHERE IT STARTED WRITES NOTHING. Snapping means most small drags end
//      on their own slot; a PATCH for a zero-minute move would be a write with no change in it.
//   3. THE GHOST IS THE PROPOSAL, AND THE BLOCK HAS NOT MOVED YET. `drag` describes where the
//      block WOULD go. Nothing in the cache changes until the pointer is released.
//   4. ESCAPE CANCELS. A drag in progress is abandonable without a write, because the moment
//      you realise you grabbed the wrong block is the moment you are still holding it.
// ============================================================================

export type BlockDragMode = 'move' | 'resize'

/** Every grid column stamps its own day here, so a drag can find the day under the pointer. */
export const CAL_COLUMN_ATTR = 'data-cal-day'
/** …and every block stamps itself, so a resize handle can measure the block it belongs to. */
export const CAL_BLOCK_ATTR = 'data-cal-block'

export interface BlockDrag {
  block: CalendarBlock
  mode: BlockDragMode
  /** The day the proposal is over. Only a move can change it; a resize is vertical. */
  date: ISODate
  startMinutes: number
  endMinutes: number
  /** False until the pointer passes the threshold — see rule 1. */
  moved: boolean
}

/** What a released drag asks the caller to save. Emitted at most once per press. */
export interface BlockDrop {
  block: CalendarBlock
  date: ISODate
  startMinutes: number
  endMinutes: number
}

interface Grab {
  block: CalendarBlock
  mode: BlockDragMode
  pointerId: number
  originX: number
  originY: number
  /**
   * Pixels between the pointer and the edge it is holding — the block's top for a move, its
   * bottom for a resize. Without it the block jumps so its edge meets the cursor the instant the
   * drag starts, which reads as the app throwing the block rather than the owner moving it.
   */
  grabOffsetPx: number
}

/**
 * The column nearest the pointer horizontally, where its grid body starts, and HOW TALL IT IS.
 *
 * The height is what makes the drag correct at the app's default 130% UI scale: it is the
 * rendered 15-hour window in the same units `clientY` arrives in, so `offsetToRawMinutes` never
 * has to know what the zoom factor is. See its own comment for the hour this used to cost.
 */
function columnAt(clientX: number): { date: ISODate; top: number; height: number } | null {
  const columns = [...document.querySelectorAll<HTMLElement>(`[${CAL_COLUMN_ATTR}]`)]
  let best: HTMLElement | null = null
  let bestBox: DOMRect | null = null
  let bestDistance = Number.POSITIVE_INFINITY

  for (const column of columns) {
    const box = column.getBoundingClientRect()
    // NEAREST, NOT CONTAINING. A pointer over the hour gutter, or past the right edge of the
    // card, still belongs to a day — the one it is closest to. Returning null there would make
    // the ghost vanish mid-drag, which is the grid pretending it lost track of the pointer.
    const dx = clientX < box.left ? box.left - clientX : clientX > box.right ? clientX - box.right : 0
    if (dx < bestDistance) {
      bestDistance = dx
      best = column
      bestBox = box
    }
  }

  const date = best?.getAttribute(CAL_COLUMN_ATTR)
  return date && bestBox
    ? { date: date as ISODate, top: bestBox.top, height: bestBox.height }
    : null
}

export interface BlockDragController {
  /** The live proposal, or null when nothing is being dragged. */
  drag: BlockDrag | null
  /** Called from a block's pointerdown (move) or its bottom handle's (resize). */
  begin: (block: CalendarBlock, mode: BlockDragMode, event: ReactPointerEvent<HTMLElement>) => void
  /**
   * True exactly once, when the click that is about to fire is the tail of a drag rather than a
   * real click. The caller returns early instead of opening the composer.
   */
  consumeClick: () => boolean
}

export function useBlockDrag(onDrop: (drop: BlockDrop) => void): BlockDragController {
  const [drag, setDrag] = useState<BlockDrag | null>(null)
  const grab = useRef<Grab | null>(null)
  const live = useRef<BlockDrag | null>(null)
  const swallowClick = useRef(false)
  const dropRef = useRef(onDrop)
  dropRef.current = onDrop

  const begin = useCallback(
    (block: CalendarBlock, mode: BlockDragMode, event: ReactPointerEvent<HTMLElement>) => {
      // Left button only. A right-click is a context menu, and a middle-click is a paste on
      // some platforms — neither is a request to move an hour of somebody's week.
      if (event.button !== 0) return
      const chip = event.currentTarget.closest<HTMLElement>(`[${CAL_BLOCK_ATTR}]`)
      if (!chip) return
      const box = chip.getBoundingClientRect()

      // A fresh press clears any flag a previous drag left behind — belt and braces for the case
      // where a drag ended outside the block and no click ever followed it.
      swallowClick.current = false

      // POINTER CAPTURE IS LOAD-BEARING, NOT A TIDINESS MEASURE. Without it a drag that releases
      // over empty grid — which is most drags, since the point is to move the block somewhere it
      // is not — fires its click at the nearest common ancestor of press and release, i.e. the
      // COLUMN. The column reads that as "clicked empty grid" and opens a new-event composer on
      // top of the move that just happened. With capture, both the pointer stream and the click
      // stay on the element that was pressed, where `consumeClick()` can swallow them.
      //
      // Wrapped because capture is an OPTIMISATION OF CORRECTNESS, not a precondition: a pointer
      // id the browser no longer considers active throws NotFoundError, and a drag that works
      // slightly worse beats a drag that throws on pointerdown.
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        /* the pointer is gone; the window listeners below still see the rest of the gesture */
      }
      grab.current = {
        block,
        mode,
        pointerId: event.pointerId,
        originX: event.clientX,
        originY: event.clientY,
        grabOffsetPx: event.clientY - (mode === 'move' ? box.top : box.bottom),
      }
      const seed: BlockDrag = {
        block,
        mode,
        date: block.date,
        startMinutes: block.startMinutes,
        endMinutes: block.endMinutes,
        moved: false,
      }
      live.current = seed
      setDrag(seed)
    },
    [],
  )

  // THE DEPENDENCY IS "IS A DRAG RUNNING", NOT THE DRAG ITSELF. Every proposal lives in a ref as
  // well as in state, so these four listeners are attached once when the press starts and removed
  // once when it ends — rather than being torn down and rebuilt on every pointermove, which is
  // both wasteful and a good way to miss the pointerup that arrives between two renders.
  const active = drag !== null
  useEffect(() => {
    if (!active) return

    const finish = () => {
      grab.current = null
      live.current = null
      setDrag(null)
    }

    const onPointerMove = (event: PointerEvent) => {
      const held = grab.current
      const current = live.current
      if (!held || !current || event.pointerId !== held.pointerId) return

      const travelled = Math.hypot(event.clientX - held.originX, event.clientY - held.originY)
      if (!current.moved && travelled < CALENDAR_DRAG_THRESHOLD_PX) return

      const column = columnAt(event.clientX)
      if (!column) return
      const edgeMinutes = offsetToRawMinutes(
        event.clientY - held.grabOffsetPx - column.top,
        column.height,
      )
      const from = { startMinutes: held.block.startMinutes, endMinutes: held.block.endMinutes }

      // A RESIZE STAYS ON ITS OWN DAY. Dragging a bottom edge sideways is a hand wobbling, not a
      // request for a block that spans midnight — and the drawn window (7 AM – 10 PM) has no
      // midnight in it to span.
      const span = held.mode === 'move' ? movedSpan(from, edgeMinutes) : resizedSpan(from, edgeMinutes)
      const date = held.mode === 'move' ? column.date : held.block.date

      const next: BlockDrag = { ...current, ...span, date, moved: true }
      if (
        current.moved &&
        current.date === next.date &&
        isSameSpan(current, next)
      ) {
        return
      }
      live.current = next
      setDrag(next)
    }

    const onPointerUp = (event: PointerEvent) => {
      const held = grab.current
      const current = live.current
      if (!held || !current || event.pointerId !== held.pointerId) return
      finish()

      if (!current.moved) return
      // The press travelled, so whatever click follows it is drag debris and not an edit.
      swallowClick.current = true

      const from = { startMinutes: held.block.startMinutes, endMinutes: held.block.endMinutes }
      if (current.date === held.block.date && isSameSpan(from, current)) return
      dropRef.current({
        block: held.block,
        date: current.date,
        startMinutes: current.startMinutes,
        endMinutes: current.endMinutes,
      })
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (live.current?.moved) swallowClick.current = true
      finish()
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [active])

  const consumeClick = useCallback(() => {
    if (!swallowClick.current) return false
    swallowClick.current = false
    return true
  }, [])

  return { drag, begin, consumeClick }
}
